import { useState, useEffect, useRef } from "react";
import RiskForm from "./components/RiskForm";
import RiskChart from "./components/RiskChart";
import RiskMatrix from "./components/RiskMatrix";
import RiskTable from "./components/RiskTable";
import AIModal from "./components/AIModal";
import PresentationMode from "./components/PresentationMode";
import "./App.css";

const SAMPLE_RISKS = [
  { id: 1, name: "Server Outage",   cat: "Technical",    owner: "DevOps",       prob: 3, imp: 5, sev: 8, occ: 3, det: 4, score: 15, rpn: 96,  mit: "Implement redundant infrastructure and automated failover. Schedule quarterly DR drills." },
  { id: 2, name: "Data Breach",     cat: "Regulatory",   owner: "Security",     prob: 2, imp: 5, sev: 9, occ: 2, det: 3, score: 10, rpn: 54,  mit: "Enable MFA, encrypt data at rest, run annual pen tests." },
  { id: 3, name: "Budget Overrun",  cat: "Financial",    owner: "PMO",          prob: 4, imp: 3, sev: 6, occ: 4, det: 5, score: 12, rpn: 120, mit: "Monthly budget reviews with 10% contingency reserved." },
  { id: 4, name: "Key Staff Loss",  cat: "Operational",  owner: "HR",           prob: 3, imp: 4, sev: 7, occ: 3, det: 6, score: 12, rpn: 126, mit: "Document critical processes, cross-train, competitive retention packages." },
  { id: 5, name: "Vendor Failure",  cat: "Strategic",    owner: "Procurement",  prob: 2, imp: 3, sev: 5, occ: 2, det: 4, score: 6,  rpn: 40,  mit: "Qualify backup vendors, include SLAs in contracts." },
];

const TABS = ["Register", "Chart", "Matrix"];
let _nextId = 6;
const uid = () => _nextId++;

export default function App() {
  const [risks, setRisks]     = useState(SAMPLE_RISKS);
  const [tab, setTab]         = useState("Register");
  const [aiOpen, setAiOpen]   = useState(false);
  const [presOpen, setPresOpen] = useState(false);
  const [toast, setToast]     = useState({ show: false, msg: "" });
  const toastTimer            = useRef(null);

  // Load Chart.js once
  useEffect(() => {
    if (window.Chart) return;
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
    document.body.appendChild(s);
  }, []);

  const showToast = (msg) => {
    clearTimeout(toastTimer.current);
    setToast({ show: true, msg });
    toastTimer.current = setTimeout(() => setToast({ show: false, msg: "" }), 2600);
  };

  // ── Risk CRUD ──────────────────────────────────────────────────
  const addRisk = (data) => {
    const score = data.prob * data.imp;
    const rpn   = (data.sev || 5) * (data.occ || 5) * (data.det || 5);
    setRisks((p) => [...p, { id: uid(), ...data, score, rpn }]);
    showToast("Risk added");
  };

  const addRisksBatch = (batch) => {
    const rows = batch.map((d) => ({
      id: uid(), ...d,
      score: d.prob * d.imp,
      rpn:   (d.sev || 5) * (d.occ || 5) * (d.det || 5),
    }));
    setRisks((p) => [...p, ...rows]);
    showToast(`${rows.length} risk${rows.length > 1 ? "s" : ""} added from AI`);
  };

  const deleteRisk = (id) => setRisks((p) => p.filter((r) => r.id !== id));

  const updateMit = (id, mit) =>
    setRisks((p) => p.map((r) => (r.id === id ? { ...r, mit } : r)));

  // ── Stats ──────────────────────────────────────────────────────
  const critical = risks.filter((r) => r.score > 15 || r.rpn > 500).length;
  const high     = risks.filter((r) => (r.score > 8 && r.score <= 15) || (r.rpn > 200 && r.rpn <= 500)).length;
  const avgScore = risks.length ? Math.round(risks.reduce((s, r) => s + r.score, 0) / risks.length) : null;
  const avgRPN   = risks.length ? Math.round(risks.reduce((s, r) => s + r.rpn,   0) / risks.length) : null;

  // ── CSV Export ─────────────────────────────────────────────────
  const exportCSV = () => {
    if (!risks.length) { showToast("No risks to export"); return; }
    const levelOf = (r) =>
      r.score > 15 || r.rpn > 500 ? "Critical"
      : r.score > 8  || r.rpn > 200 ? "High"
      : r.score > 4  ? "Medium" : "Low";
    const headers = ["Name","Category","Owner","Probability","Impact","Score","Severity","Occurrence","Detection","RPN","Level","Mitigation"];
    const rows = risks.map((r) =>
      [r.name, r.cat, r.owner || "", r.prob, r.imp, r.score, r.sev, r.occ, r.det, r.rpn, levelOf(r), r.mit || ""]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: "risk-register.csv" });
    a.click();
    showToast("CSV downloaded");
  };

  // ── PDF Export ─────────────────────────────────────────────────
  const exportPDF = () => {
    window.print();
  };

  return (
    <div className="app">
      {/* ── TOPBAR ── */}
      <header className="topbar no-print">
        <div className="logo">
          <div className="logo-mark">R</div>
          <span className="logo-text">RiskIQ</span>
          <span className="logo-badge">PRO</span>
        </div>
        <div className="topbar-actions">
          <button className="btn"             onClick={exportCSV}>↓ CSV</button>
          <button className="btn"             onClick={exportPDF}>⎙ PDF</button>
          <button className="btn btn-present" onClick={() => setPresOpen(true)}>⊡ Present</button>
        </div>
      </header>

      <div className="body-layout">
        {/* ── SIDEBAR ── */}
        <aside className="sidebar no-print">
          <RiskForm addRisk={addRisk} />
        </aside>

        {/* ── MAIN ── */}
        <main className="main-content">
          {/* Stat cards */}
          <div className="stats-row">
            {[
              { label: "Total",     value: risks.length,  cls: ""         },
              { label: "Critical",  value: critical,      cls: "c-danger" },
              { label: "High",      value: high,          cls: "c-warn"   },
              { label: "Avg Score", value: avgScore ?? "—", cls: "c-accent" },
              { label: "Avg RPN",   value: avgRPN   ?? "—", cls: ""       },
            ].map(({ label, value, cls }) => (
              <div className="stat-card" key={label}>
                <div className={`stat-value ${cls}`}>{value}</div>
                <div className="stat-label">{label}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="tabs-bar no-print">
            {TABS.map((t) => (
              <button
                key={t}
                className={`tab-btn ${tab === t ? "tab-active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Tab panels */}
          <div className="tab-panel">
            {tab === "Register" && (
              <RiskTable risks={risks} deleteRisk={deleteRisk} updateMit={updateMit} showToast={showToast} />
            )}
            {tab === "Chart" && <RiskChart risks={risks} />}
            {tab === "Matrix" && <RiskMatrix risks={risks} />}
          </div>
        </main>
      </div>

      {/* ── MODALS ── */}
      {aiOpen   && <AIModal           onClose={() => setAiOpen(false)}  onAdd={addRisksBatch} />}
      {presOpen && <PresentationMode  risks={risks}                     onClose={() => setPresOpen(false)} />}

      {/* ── TOAST ── */}
      <div className={`toast ${toast.show ? "toast-show" : ""}`}>
        <div className="toast-dot" />{toast.msg}
      </div>
    </div>
  );
}